import React, { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import { useTheme } from '@mui/material/styles';
import Logo from '../DTP/HeaderNavigation/Logo';
import { AppBarLink, AppBarMenuItem } from './CRHeaderNavigation.style';
import { ChildLinkItem, CRNavFields } from './CRHeaderNavigation.types';
import { useRouter } from 'next/router';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import PermIdentityOutlined from '@mui/icons-material/PermIdentityOutlined';
import { LinkField, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import { getQuery, getQueryParams } from '../../lib/utils/common';
import { SIGN_OUT } from '../../lib/constant';
import ButtonDiscreet from '../shared/Button/ButtonDiscreet';
import { usePathname } from 'next/navigation';
import UiText from '../shared/Typography/UiText';

const HCP_PAGE = 'For Healthcare Professional';
const PROFILE = 'myProfile';

const fallbackLink: LinkField = { value: { href: '', text: '' } };
const fallbackImg: ImageField = { value: { src: '' } };
const CRDesktopNav: React.FC<CRNavFields> = (props) => {
  const router = useRouter();
  const pathname = usePathname();
  const { query } = router;
  const theme = useTheme();
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElProfile, setAnchorElProfile] = React.useState<null | HTMLElement>(null);
  const profileOpen = Boolean(anchorElProfile);

  useEffect(() => {
    updateActiveMenuItem();
  }, [pathname]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.currentTarget.getAttribute('id') === 'profile-button') {
      setAnchorElProfile(event.currentTarget);
      return;
    } else {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    anchorEl && setAnchorEl(null);
    anchorElProfile && setAnchorElProfile(null);
  };

  function gotoPage(url?: string) {
    router.push({
      pathname: url,
      query: getQuery(query),
    });
  }

  function hardReloadPage(url?: string) {
    if (!url || typeof window === 'undefined') {
      return;
    }

    const nextQuery = getQueryParams(query);
    const separator = url.includes('?') ? '&' : '?';
    window.location.assign(nextQuery ? `${url}${separator}${nextQuery}` : url);
  }

  const ChildMenu: React.FC<{
    navLinks: Array<ChildLinkItem>;
    anchorElm: any;
    id: string;
    btnId: string;
    isOpen: boolean;
    fieldItem?: any;
  }> = (propsChildMenu) => {
    const onClickMenuItem = (e: any, item: any) => {
      if (item.name === SIGN_OUT) {
        props?.openProfileModal({
          open: false,
          profileFields: propsChildMenu?.fieldItem || item.fields,
          signOutOpen: true,
        });
        handleClose();
        return;
      }
      if (propsChildMenu?.fieldItem?.hasOwnProperty(PROFILE)) {
        e.preventDefault();
        hardReloadPage(item.fields?.link?.value.href);
      } else {
        gotoPage(item.fields?.link?.value.href);
      }
      handleClose();
    };
    if (Array.isArray(propsChildMenu.navLinks) && propsChildMenu.navLinks.length > 0) {
      return (
        <Menu
          sx={{
            marginTop: '10px',
          }}
          id={propsChildMenu.id}
          anchorEl={propsChildMenu.anchorElm}
          open={propsChildMenu.isOpen}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': propsChildMenu.btnId,
          }}
        >
          {propsChildMenu.navLinks.map((item, index) => {
            return (
              <AppBarMenuItem
                id={item.fields?.link?.value?.text}
                key={`${propsChildMenu.btnId}-${index}`}
                onClick={(e) => onClickMenuItem(e, item)}
              >
                {item.name === SIGN_OUT ? (
                  <UiText color="primary">{item.fields?.link?.value.text}</UiText>
                ) : (
                  <AppBarLink
                    id={item.fields?.link?.value?.text}
                    field={item.fields?.link || fallbackLink}
                  ></AppBarLink>
                )}
              </AppBarMenuItem>
            );
          })}
        </Menu>
      );
    }
    return <></>;
  };

  function updateActiveMenuItem() {
    if (pathname === '/') {
      setActiveMenuItem(null);
      return;
    }

    function subMenuItemLookup(children?: ChildLinkItem[]) {
      if (!children) return false;

      const isActive = children?.some((x) => {
        const itemPath = x.fields?.link?.value.href;
        return itemPath?.includes(pathname);
      });

      return isActive;
    }

    props.navigationLinks?.forEach((item) => {
      const found = subMenuItemLookup(item.fields?.childNavList);
      if (found) {
        setActiveMenuItem(item.id);
      }
    });
  }

  return (
    <Box
      id="cr-desktop-header-navigation"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        width: '100%',
        marginLeft: '6rem',
        marginRight: '5rem',
        [theme.breakpoints.down(1150)]: {
          marginLeft: '4rem',
          marginRight: '3rem',
        },
      }}
    >
      <Box>
        <Logo
          id={'cr-desktop-logo'}
          imageField={props.headerLogo || fallbackImg}
          ariaLabel={props?.headerLogoAriaLabel?.value as string}
        />
      </Box>
      <Box
        id="desktop-main-navigation-wrapper"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
      >
        {props.navigationLinks?.map((item, index) => {
          const childNavList = item.fields?.childNavList;
          const containSubMenu = Array.isArray(childNavList) && childNavList.length > 0;
          const isProfileLinkAsModal = item.fields?.hasOwnProperty(PROFILE);
          const isHCPLink = (item as any)?.name === HCP_PAGE;
          const open = anchorEl?.id === item.id;
          if (isProfileLinkAsModal) {
            if (!props.isAuthenticated) {
              return (
                <ButtonDiscreet
                  key={index}
                  id={'profile-button'}
                  onClick={() =>
                    props?.openProfileModal({ open: true, profileFields: item.fields })
                  }
                  color={
                    theme.buttonStyle?.discreet?.textColor.hover || theme.palette.themeColor.main
                  }
                >
                  {item.fields?.link?.value.text || ''}
                </ButtonDiscreet>
              );
            }

            return (
              <>
                <ButtonDiscreet
                  onClick={handleClick}
                  id="profile-button"
                  iconLeft={
                    <Avatar
                      alt="Profile"
                      sx={{
                        bgcolor:
                          theme.buttonStyle?.discreet?.textColor.hover ||
                          theme.palette.themeColor.main,
                        width: 35,
                        height: 35,
                      }}
                    >
                      <PermIdentityOutlined />
                    </Avatar>
                  }
                  color={
                    theme.buttonStyle?.discreet?.textColor.hover || theme.palette.themeColor.main
                  }
                  state={activeMenuItem === item.id ? 'hover' : 'default'}
                  style={{ paddingLeft: '6px' }}
                >
                  <>
                    {item.fields?.link?.value.text || ''}
                    {profileOpen ? <ArrowDropUp /> : <ArrowDropDown />}
                  </>
                </ButtonDiscreet>
                <ChildMenu
                  navLinks={item.fields?.childNavList || []}
                  anchorElm={anchorElProfile}
                  id={item?.id}
                  btnId="profile-button"
                  isOpen={profileOpen}
                  fieldItem={item.fields}
                />
              </>
            );
          }

          if (isHCPLink && props.isAuthenticated) {
            return null;
          }

          return (
            <Box key={index}>
              <ButtonDiscreet
                id={item.id}
                key={`basic-button-parent-${index}`}
                onClick={(e) =>
                  containSubMenu ? handleClick(e) : gotoPage(item.fields?.link?.value.href)
                }
                iconRight={childNavList?.length ? open ? <ArrowDropUp /> : <ArrowDropDown /> : null}
                gap="0"
                color={
                  theme.buttonStyle?.discreet?.textColor.hover || theme.palette.themeColor.main
                }
                state={activeMenuItem === item.id ? 'hover' : 'default'}
              >
                {item.fields?.link?.value.text || ''}
              </ButtonDiscreet>
              {containSubMenu && (
                <ChildMenu
                  navLinks={item.fields?.childNavList || []}
                  anchorElm={anchorEl}
                  id={item?.id}
                  btnId={item.fields?.link?.value.text || 'basic-button'}
                  isOpen={open}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
export default CRDesktopNav;
